import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { getInitials } from '../utils/format';
import Icon from '../components/Icon';
import './Chat.css';

function Chat() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let interval;

    const init = async () => {
      try {
        const [convos, msgs] = await Promise.all([
          api.getConversations(),
          api.getMessages(conversationId)
        ]);
        if (controller.signal.aborted) return;
        const conv = convos.find(c => c.id === conversationId);
        setConversation(conv);
        setMessages(msgs);
        await api.markConversationAsRead(conversationId);
      } catch (err) {
        if (!controller.signal.aborted) console.error('Failed to load conversation:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    init();
    let polling = false;
    interval = setInterval(async () => {
      if (controller.signal.aborted || polling) return;
      polling = true;
      try {
        const msgs = await api.getMessages(conversationId);
        if (!controller.signal.aborted) setMessages(msgs);
      } catch (err) {
        if (!controller.signal.aborted) console.error('Failed to load messages:', err);
      } finally {
        polling = false;
      }
    }, 5000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const message = await api.sendMessage(conversationId, newMessage.trim());
      setMessages(prev => [...prev, message]);
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };


  const getOtherParticipant = () => {
    return conversation?.participants?.find(p => p.id !== user?.id) || {};
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const groupMessagesByDate = () => {
    const groups = [];
    let currentDate = null;

    messages.forEach(message => {
      const messageDate = new Date(message.sent_at).toDateString();
      if (messageDate !== currentDate) {
        currentDate = messageDate;
        groups.push({ type: 'date', date: message.sent_at });
      }
      groups.push({ type: 'message', message });
    });

    return groups;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const other = getOtherParticipant();
  const groupedMessages = groupMessagesByDate();

  return (
    <div className="chat-page">
      <div className="chat-header">
        <Link to="/messages" className="back-button" aria-label="Back to messages">
          <Icon name="arrowLeft" size={20} />
        </Link>
        <Link to={`/golfer/${other.id}`} className="chat-user-info">
          <div className="avatar avatar-sm">
            {other.photo_url ? (
              <img src={other.photo_url} alt={other.name} />
            ) : (
              getInitials(other.name)
            )}
          </div>
          <span className="chat-user-name">{other.name || 'Unknown'}</span>
        </Link>
      </div>

      <div className="chat-messages">
        {groupedMessages.map((item, index) => {
          if (item.type === 'date') {
            return (
              <div key={`date-${index}`} className="date-separator">
                <span>{formatDate(item.date)}</span>
              </div>
            );
          }

          const message = item.message;
          const isOwn = message.sender_id === user?.id;

          return (
            <div
              key={message.id}
              className={`message ${isOwn ? 'message-own' : 'message-other'}`}
            >
              {!isOwn && (
                <div className="avatar avatar-sm">
                  {message.sender_photo ? (
                    <img src={message.sender_photo} alt={message.sender_name} />
                  ) : (
                    getInitials(message.sender_name)
                  )}
                </div>
              )}
              <div className="message-bubble">
                <p className="message-content">{message.content}</p>
                <span className="message-time">{formatTime(message.sent_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="chat-input-container">
        <input
          type="text"
          className="chat-input"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={sending}
        />
        <button
          type="submit"
          className="btn btn-primary send-button"
          disabled={!newMessage.trim() || sending}
        >
          {sending ? '...' : 'Send'}
        </button>
      </form>
    </div>
  );
}

export default Chat;
